import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { useAuth } from "../../context/AuthContext";
import { api, errMsg } from "../../lib/api";
import FieldInput, { cleanLines } from "./FieldInput";
import { DataTable } from "./ui";

export default function CrudManager({ endpoint, title, columns, fields, defaults = {}, canWrite, testid, allowCreate = true, afterSave }) {
  const { canEdit } = useAuth();
  const writable = canWrite ?? canEdit;
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(endpoint).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), [endpoint]);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = Object.fromEntries(fields.map((f) => [f.name, cleanLines(f, editing[f.name])]));
    try {
      const { data } = editing.id ? await api.put(`${endpoint}/${editing.id}`, body) : await api.post(endpoint, body);
      const note = afterSave ? await afterSave(data) : "";
      (note ? toast.warning : toast.success)(note || "Saved"); setEditing(null); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const remove = async (row) => {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    try { await api.delete(`${endpoint}/${row.id}`); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const actions = writable ? [{ key: "_actions", label: "Actions", render: (r) => (
    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
      <button type="button" className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={() => setEditing({ ...r })} data-testid={`${testid}-edit-${r.id}`} aria-label="Edit"><Pencil size={14} /></button>
      <button type="button" className="rounded border px-2 py-1 text-xs text-red-700 hover:bg-red-50" onClick={() => remove(r)} data-testid={`${testid}-delete-${r.id}`} aria-label="Delete"><Trash2 size={14} /></button>
    </div>) }] : [];

  return (
    <section data-testid={testid}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-xl text-navy">{title}</h2>
        {writable && allowCreate && <button type="button" className="btn-primary btn-sm" onClick={() => setEditing({ ...defaults })} data-testid={`${testid}-add-btn`}><Plus size={16} aria-hidden="true" /> Add</button>}
      </div>
      <DataTable columns={[...columns, ...actions]} rows={rows} testid={`${testid}-table`} />
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid={`${testid}-dialog`}>
          <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "Add"} {title.replace(/s$/, "")}</DialogTitle></DialogHeader>
          {editing && (
            <form onSubmit={save} className="space-y-4">
              {fields.map((f) => (
                <div key={f.name}>
                  <label htmlFor={`f-${f.name}`} className="field-label">{f.label}</label>
                  <FieldInput field={f} value={editing[f.name]} onChange={(v) => setEditing((s) => ({ ...s, [f.name]: v }))} />
                  {f.help && <p className="mt-1 text-xs text-[#4A5568]">{f.help}</p>}
                </div>
              ))}
              <button type="submit" className="btn-primary" disabled={busy} data-testid={`${testid}-save-btn`}>{busy ? "Saving…" : "Save"}</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
